<script>
	// NPM Modules
	import dayjs from 'dayjs';
	import { createEventDispatcher, tick } from 'svelte';
	// Stores
	import MarketStore from '@/Stores/Sport/MarketStore.js';
	import SportSelectionStore from '@/Stores/Sport/SportSelectionStore.js';
	import ClassificationStore from '@/Stores/Racing/ClassificationStore';
	import CompetitionStore from '@/Stores/Sport/CompetitionStore';
	// Components
	import Icon from '@/Components/Icon.svelte';
	import Prompt from '@/Components/Prompt.svelte';
	import FontIcon from '@/Components/FontIcon.svelte';
	import LoadingButton from '@/Components/LoadingButton.svelte';
	import TextInput from '@/Components/TextInput.svelte';
	// Partials
	import MarketBets from '@/Partials/Sport/Markets/_MarketBets.svelte';
	import ManualResultToggle from '@/Partials/Sport/Markets/_ManualResultToggle.svelte';
	import Boost from '@/Partials/Sport/Markets/_Boost.svelte';
	import Bonus from '@/Partials/Sport/Markets/_Bonus.svelte';
	import Multis from '@/Partials/Sport/Markets/_Multi.svelte';
	import Cashout from '@/Partials/Sport/Markets/_Cashout.svelte';
	import OverridePrice from '@/Partials/Sport/Markets/_OverridePrice.svelte';
	import AdjustSelectionLiability from '@/Partials/Liabilities/_AdjustSelectionLiability.svelte';
	import AdjustMarketLine from '@/Partials/Sport/Markets/_AdjustMarketLine.svelte';
	import CreateMarketSelections from '@/Partials/Sport/Markets/_CreateMarketSelections.svelte';
	import DeriveMarket from '@/Partials/Sport/Markets/_DeriveMarket.svelte';
	// Services
	import { notify } from '@/Services/Notify.js';
	import { CurrencyFormat, NumberFormat } from '@/Services/Helpers';
	// Constants
	import { Status, StatusName, RollingType } from '@/Constants/Market.js';
	import { SportSelectionStatus } from '@/Constants/SportSelection.js';

	const dispatch = createEventDispatcher();

	export let event;
	export let market;
	export let marketGroupId;
	export let derivedMarketGroups;
	export let resultMarket = undefined;
	export let showManualResult = undefined;

	const StatusColours = {
		[Status.Open]: 'green-500',
		[Status.Suspended]: 'orange-400',
		[Status.Closed]: 'red-600',
		[Status.Pending]: 'gray-500',
		[Status.Resulted]: 'purple-500'
	};

	$: selections = market && $SportSelectionStore && $SportSelectionStore.where('market_id', market.id);
	$: statusLabel = market && getStatusLabel();
	$: hasLockedPrices = market && $SportSelectionStore && getHasLockedPrices();
	$: (market || $SportSelectionStore) && setupOverridePrices();
	$: selections && overridePrices && calcTotalPercentage();
	// once they start overriding prices, they have this long before it resets
	$: isEditingPrices &&
		setTimeout(() => {
			if (!market.is_manual) {
				isEditingPrices = false;
			}
		}, 60 * 1000);
	$: isMarketResulted = market && market.status.id === Status.Resulted;
	$: marketCanBeUnresulted =
		market && (market.resulted_at != null ? dayjs().diff(dayjs(market.resulted_at), 'hour') < 48 : true); //The race is within 48 hours of starting
	$: showOverrides = market && !isMarketResulted;
	$: classification = event && $CompetitionStore && $ClassificationStore && getEventClassification();
	$: currentLiabilityLimit = event && market && getCurrentLiabilityLimit();
	$: currentMultiLiabilityLimit = event && market && getCurrentMultiLiabilityLimit();
	$: filteredSelections = market && selections && getFilteredSelections(market);

	let totalPercent = 0;
	let showBets = false;
	let isEditingPrices = false;
	let savingPrices = false;
	let showPromptUnresultMarket = false;
	let showPromptUnresultSelection = false;
	let showPromptMarketLiabilityAcceptance = false;
	let showPromptDeleteMarket = false;
	let showDeriveMarket = false;
	let promptUnresultSelection;
	let showLiabilityAdjustment = false;
	let showMarketLineAdjustment = false;
	let showCreateMarketSelections = false;
	let displayEditSelection = false;
	let editingMarketSelection = null;
	let overridePrices = {};

	function getCurrentLiabilityLimit() {
		if (market.liabilities && market.liabilities.settings && market.liabilities.settings.selection_limit) {
			return CurrencyFormat(market.liabilities.settings.selection_limit);
		}
		if (classification && classification.selection_liability_limit !== null) {
			return CurrencyFormat(classification.selection_liability_limit);
		}

		return '';
	}

	function getCurrentMultiLiabilityLimit() {
		if (market.liabilities && market.liabilities.settings && market.liabilities.settings.multi_selections_limit) {
			return CurrencyFormat(market.liabilities.settings.multi_selections_limit);
		}
		if (classification && classification.multi_selections_liability_limit !== null) {
			return CurrencyFormat(classification.multi_selections_liability_limit);
		}

		return '';
	}

	const resetOverridePrices = async () => {
		isEditingPrices = false;
		await tick();
		isEditingPrices = false;
	};

	function getEventClassification() {
		let competition = $CompetitionStore.firstWhere('id', event.competition.id);
		if (competition !== null) {
			return $ClassificationStore.firstWhere('id', competition.classification.id.toString());
		}
	}

	function overrideStatus(statusId) {
		if (market.override_status && market.override_status.id === statusId) statusId = null;
		MarketStore.updateMarketOverrideStatus(market.id, statusId)
			.then(() => {
				notify({
					type: 'success',
					title: market.name,
					message: statusId
						? `Market Status Override updated to ${StatusName[statusId]}`
						: 'Market Status Override has been removed',
					timeout: 3
				});
				//update the markets selections
				MarketStore.getMarketWithSelections(market.id);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to update Override Status',
					timeout: 5
				});
			});
	}

	function onManualResultClick() {
		resultMarket = market;
		showManualResult = true;
	}

	function getStatusLabel() {
		return !market.feed_status
			? `Status: ${market.status.name}`
			: market.override_status
				? `Override status: ${market.override_status.name}, Feed status: ${market.feed_status.name}`
				: `Feed status: ${market.feed_status.name}`;
	}

	function getHasLockedPrices() {
		return !!$SportSelectionStore.where('market_id', market.id).where('override_price').count();
	}

	function setupOverridePrices() {
		if (isEditingPrices) return;
		if (!market) return;
		overridePrices = {};
		$SportSelectionStore.where('market_id', market.id).each((selection) => {
			overridePrices[selection.id] = selection.override_price
				? (selection.override_price / 100).toFixed(2)
				: (selection.price / 100).toFixed(2);
		});
	}

	function saveOverridePrices() {
		if (market.status.id === Status.Resulted) return;
		calcTotalPercentage();
		if (savingPrices == false && totalPercent < 100) {
			showPromptMarketLiabilityAcceptance = true;
			return;
		}
		savingPrices = true;
		let selectionPrices = [];
		Object.entries(overridePrices).forEach((selection) => {
			if ((selection[1] * 100).toFixed(0) !== '0') {
				selectionPrices.push({
					selection_id: selection[0],
					override_price: (selection[1] * 100).toFixed(0)
				});
			}
		});
		MarketStore.updateMarketOverridePrices(market.id, selectionPrices).finally(() => {
			isEditingPrices = false;
			savingPrices = false;
		});
	}

	function acceptMarketLiabilityPrompt() {
		savingPrices = true;
		saveOverridePrices();
	}

	function changeLock() {
		if (hasLockedPrices) {
			MarketStore.removeMarketOverridePrices(market.id);
		} else {
			saveOverridePrices();
		}
	}

	function formatLiability(selection, liability) {
		if (!selection.liabilities) return '-';
		return NumberFormat(selection.liabilities[liability]);
	}

	function calcSinglePercentage(selection) {
		if (
			(selection.id in overridePrices && overridePrices[selection.id]) ||
			selection.price ||
			selection.override_price
		) {
			if (
				(selection.id in overridePrices
					? overridePrices[selection.id]
					: selection.override_price
						? selection.override_price / 100
						: selection.price / 100) == 0
			) {
				return 0;
			}
			return NumberFormat(
				100 /
					(selection.id in overridePrices
						? overridePrices[selection.id]
						: selection.override_price
							? selection.override_price / 100
							: selection.price / 100),
				true
			);
		}

		return 0;
	}

	function calcTotalPercentage() {
		totalPercent = 0;
		selections.whereIn('status.id', [SportSelectionStatus.Pending, SportSelectionStatus.Active]).each((selection) => {
			if (
				!selectionIsSuspended(selection) &&
				((selection.id in overridePrices &&
					overridePrices[selection.id] !== null &&
					overridePrices[selection.id] * 100 > 100) ||
					(selection.override_price !== null && parseInt(selection.override_price) > 100) ||
					(selection.override_price === null && parseInt(selection.price) > 100))
			) {
				totalPercent += parseFloat(calcSinglePercentage(selection));
			}
		});
	}

	function transformLine(selection, market) {
		if (market.rolling_type) {
			if (market.rolling_type.id === RollingType.UnderOver && market.line) {
				return `${selection.line}`;
			} else if (market.rolling_type.id === RollingType.Handicap && selection.line) {
				return `(${selection.display_line})`;
			} else if (market.rolling_type.id === RollingType.MatchWinnerUnderOver && selection.line) {
				return `${selection.display_line}`;
			}
		}
		return '';
	}

	function selectionIsSuspended(selection) {
		return (
			(market.status.id === Status.Suspended && !market.is_manual) ||
			!selection.tradable ||
			(selection.price === 100 && !selection.override_price) ||
			(selection.override_price && parseInt(selection.override_price) === 100)
		);
	}

	function selectionHasInvalidPrice(selection) {
		return !selection.price || selection.price === 0;
	}

	function selectionIsResulted(selection) {
		return [SportSelectionStatus.Win, SportSelectionStatus.Lost, SportSelectionStatus.Void].includes(
			selection.status.id
		);
	}
	function selectionIsPending(selection) {
		return [SportSelectionStatus.Pending].includes(selection.status.id);
	}

	function deleteMarket(market) {
		market
			.delete()
			.then(() => {
				notify({
					type: 'success',
					title: market.name,
					message: 'Market successfully deleted',
					timeout: 3
				});
				MarketStore.getMarketWithSelections(market.id);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to delete market',
					timeout: 5
				});
			});
	}

	function unresultMarket(market) {
		market
			.unresult()
			.then(() => {
				notify({
					type: 'success',
					title: market.name,
					message: 'Market successfully unresulted',
					timeout: 3
				});
				MarketStore.getMarketWithSelections(market.id);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to unresult market',
					timeout: 5
				});
			});
	}

	function unresultSelection(selection) {
		selection
			.unresult()
			.then(() => {
				notify({
					type: 'success',
					title: selection.name,
					message: 'Selection successfully unresulted',
					timeout: 3
				});
				MarketStore.getMarketWithSelections(market.id);
			})
			.catch((e) => {
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to unresult selection',
					timeout: 5
				});
			});
	}

	function getFilteredSelections() {
		if (market.type.price_order) {
			if (market.is_manual) {
				return selections
					.sortBy((selection) => {
						if (selection.override_price !== null) {
							return selection.override_price;
						}
						return selection.price;
					})
					.sort(sortSuspendedSelections)
					.all();
			}
			return selections
				.sortBy((selection) => {
					if (selection.override_price !== null) {
						return selection.override_price;
					}
					return selection.price;
				})
				.all();
		}
		if (market.is_manual) {
			return selections.sort(sortSuspendedSelections).all();
		}
		return selections.all();
	}

	const sortSuspendedSelections = (a, b) => {
		// Check if a's price is 1.00 or a's override_price is set and equals 1.00
		const aShouldBeMoved = a.override_price ? a.override_price == 100 : a.price == 100;
		// Check if b's price is 1.00 or b's override_price is set and equals 1.00
		const bShouldBeMoved = b.override_price ? b.override_price == 100 : b.price == 100;

		// If only a should be moved, move it to the end
		if (aShouldBeMoved && !bShouldBeMoved) {
			return 1;
		}
		// If only b should be moved, keep it in place or move a before b
		else if (!aShouldBeMoved && bShouldBeMoved) {
			return -1;
		}
		// Otherwise, keep the order unchanged
		else {
			return 0;
		}
	};

	let statusLabel;

	function showEditSelection(selection) {
		if (market.is_manual) {
			displayEditSelection = selection.id;
		}
	}

	const refreshMarkets = () => {
		dispatch('refreshMarkets');
	};

	let currentSelectionName = null;
	let updatingSelectionName = false;

	function saveSelectionName(selection) {
		updatingSelectionName = true;
		selection
			.updateSelectionName(selection.name)
			.then(() => {
				notify({
					type: 'success',
					title: selection.name,
					message: 'Selection name successfully changed',
					timeout: 3
				});
				editingMarketSelection = null;
				currentSelectionName = null;
				displayEditSelection = null;
			})
			.catch((e) => {
				selection.name = currentSelectionName;
				notify({
					type: 'danger',
					title: 'Error!',
					message: (e.error && e.error[0]) || 'Failed to change selection name',
					timeout: 5
				});
			})
			.finally(() => {
				updatingSelectionName = false;
			});
	}

	const canDeriveMarket = (marketTypeId) => {
		let containsMarketTypeId = false;
		if (derivedMarketGroups) {
			derivedMarketGroups.each((derivedMarketGroup) => {
				if (
					derivedMarketGroup.market_types.includes(marketTypeId.toString()) ||
					derivedMarketGroup.market_types.includes('all')
				) {
					containsMarketTypeId = true;
					return false;
				}
			});
		}
		return containsMarketTypeId;
	};
</script>

{#if showLiabilityAdjustment}
	<AdjustSelectionLiability bind:open={showLiabilityAdjustment} {event} {market} {marketGroupId} />
{/if}
{#if showMarketLineAdjustment}
	<AdjustMarketLine bind:open={showMarketLineAdjustment} {event} {market} {marketGroupId} />
{/if}
{#if showCreateMarketSelections}
	<CreateMarketSelections
		bind:open={showCreateMarketSelections}
		{market}
		on:created={() => {
			MarketStore.getMarketWithSelections(market.id);
		}}
	/>
{/if}
{#if showPromptUnresultMarket}
	<Prompt
		bind:open={showPromptUnresultMarket}
		title="Confirm unresulting market"
		onConfirm={() => unresultMarket(market)}
	>
		<div class="m-3">
			<span class="text-xl">
				This will un-result all the bets already placed and cancel withdrawals, only do this when you have received
				incorrect results
			</span>
			<br />
			<br />
			<span class="font-semibold text-xl">Are you sure you want to unfinalise the market?</span>
		</div>
	</Prompt>
{/if}
{#if showPromptUnresultSelection}
	<Prompt
		bind:open={showPromptUnresultSelection}
		title="Confirm unresulting selection"
		onConfirm={() => unresultSelection(promptUnresultSelection)}
	>
		<div class="m-3">
			<span class="text-xl">
				This will un-result all the bets already placed and cancel withdrawals, only do this when you have received
				incorrect results
			</span>
			<br />
			<br />
			<span class="font-semibold text-xl">Are you sure you want to unfinalise the selection?</span>
		</div>
	</Prompt>
{/if}
{#if showPromptDeleteMarket}
	<Prompt bind:open={showPromptDeleteMarket} title="" onConfirm={() => deleteMarket(market)}>
		<div class="m-3 mb-6">
			<span class="text-xl">
				<span class="font-semibold text-xl">Are you sure you want to delete the market?</span>
			</span>
		</div>
	</Prompt>
{/if}
{#if showPromptMarketLiabilityAcceptance}
	<Prompt
		bind:open={showPromptMarketLiabilityAcceptance}
		title="Confirm market liability acceptance"
		onConfirm={() => acceptMarketLiabilityPrompt()}
	>
		<div class="m-3">
			<span class="text-xl"> This action will make the market liability below 100%. </span>
			<br />
			<br />
			<span class="font-semibold text-xl">Are you sure you want to save these prices?</span>
		</div>
	</Prompt>
{/if}
{#if showDeriveMarket}
	<DeriveMarket
		bind:open={showDeriveMarket}
		{event}
		{market}
		selections={filteredSelections}
		on:create={refreshMarkets}
	/>
{/if}
{#if market}
	<div class="w-full pb-2 px-2 pt-1 mb-4 border hover:bg-gray-100 bg-white overflow-x-auto">
		<div class="flex flex-col flex-grow bg-white">
			<div class="flex flex-row gap-x-2 bg-white">
				<div class="flex flex-col justify-between flex-grow bg-white">
					<div class="grid text-sm text-center w-full market-grid bg-white">
						<!-- HEADER -->
						<!-- ROW 1 -->
						<div
							class="col-start-1 row-span-2 text-left whitespace-nowrap border-b border-row-separator py-1 flex flex-grow justify-between min-w-[400px]"
							class:col-span-2={market.is_manual && !market.rolling_type && !market.is_derived}
						>
							<div class="flex space-x-1 items-center">
								<div
									title={statusLabel}
									class="w-4 h-4 {`bg-${
										StatusColours[
											market.feed_status && market.feed_status.id ? market.feed_status.id : market.status.id
										]
									}`} rounded-full cursor-help"
								>
									{#if market.override_status && market.override_status.id}
										<div
											class="h-2 {`bg-${
												StatusColours[market.override_status.id]
											}`} border-b border-white rounded-t-full"
										/>
									{/if}
								</div>
								<div class="text-nowrap">{market.name}</div>
							</div>
							<div class="flex flex-shrink items-center">
								<div>
									{#if !market.is_derived && canDeriveMarket(market.type.id)}
										<button
											on:click={() => (showDeriveMarket = true)}
											title={market.has_derived ? 'Active derived market' : 'Derive market'}
										>
											<Icon
												name="derive"
												classes={'block w-5 h-5 inline ' +
													(market.has_derived ? 'w-6 h-6 text-purple-500' : 'text-gray-800')}
											/>
										</button>
									{/if}
									{#if market.is_manual}
										{#if market.is_derived}
											<button disabled class="btn bg-purple-500 text-white rounded-full cursor-default">
												Derived
											</button>
										{:else}
											<button disabled class="btn bg-orange-500 text-white rounded-full cursor-default">
												Manual
											</button>
										{/if}
									{/if}
									{#if isMarketResulted}
										{#if marketCanBeUnresulted}
											<button on:click={() => (showPromptUnresultMarket = true)} title="Unresult market">
												<Icon name="unfinalise" classes="block w-5 h-5 text-gray-800 inline" />
											</button>
										{:else}
											<span title="Can not unresult market">
												<Icon name="unfinalised-locked" classes="block w-5 h-5 text-gray-500 inline" />
											</span>
										{/if}
									{/if}
								</div>
							</div>
						</div>
						{#if market.is_manual && market.rolling_type}
							<div class="col-start-2 row-span-2 text-sm font-semibold border-b border-row-separator translate-y-1">
								<div class="flex flex-col justify-center items-center">
									<span>Line</span>
									<LoadingButton
										loading={savingPrices}
										on:click={() => {
											showMarketLineAdjustment = true;
										}}
										title="Click to adjust line value"
										className="w-12 py-1 text-xs btn btn-primary-action"
									>
										<span>{market.line}</span>
									</LoadingButton>
								</div>
							</div>
						{/if}
						{#if !market.is_manual || market.is_derived}
							<div class="col-start-3 mb-1 text-sm font-semibold">Lock Price</div>
						{:else}
							<div class="col-start-3 border-b border-row-separator row-span-2">
								<div class="flex items-center justify-center h-full">
									{#if market.status.id === Status.Open || (market.is_manual && market.status.id !== Status.Closed)}
										<button
											on:click={resetOverridePrices}
											title="Reset current unsaved changes to overridden prices"
											class="py-1 text-xs bg-red-500 btn btn-black"
										>
											<Icon name="revert" classes="w-4 h-4" />
										</button>
										<LoadingButton
											loading={savingPrices}
											on:click={saveOverridePrices}
											className="ml-1 h-6 py-1 text-xs btn btn-primary-action flex justify-center items-center"
										>
											Save
										</LoadingButton>
									{/if}
								</div>
							</div>
						{/if}
						<div class="col-span-2 col-start-5 text-sm font-semibold">Single</div>
						<div class="col-span-2 text-sm font-semibold">Multi</div>
						<!-- ROW 2-->
						{#if !market.is_manual || market.is_derived}
							<div class="col-start-2 border-b border-row-separator">
								{market.is_derived ? 'Derived Price' : 'Feed Price'}
							</div>
							<div class="border-b border-row-separator">
								<div class="flex items-center justify-center mb-1 space-x-1">
									{#if market.status.id === Status.Open || (market.status.id === Status.Suspended && market.is_derived)}
										{#if hasLockedPrices || savingPrices}
											<button
												on:click={changeLock}
												title="Unlock prices back to feed prices"
												class="py-1 text-xs bg-red-500 btn btn-black"
											>
												<Icon name="lock" classes="w-4 h-4" />
											</button>
											<LoadingButton
												loading={savingPrices}
												on:click={saveOverridePrices}
												className="h-6 py-1 text-xs btn btn-primary-action flex justify-center items-center"
											>
												Save
											</LoadingButton>
										{:else}
											<button on:click={changeLock} class="flex items-center py-1 text-xs btn btn-black">
												<Icon name="unlock" classes="w-4 h-4 mr-1" />
												<span>Lock</span>
											</button>
										{/if}
									{/if}
								</div>
							</div>
						{/if}
						<div class="col-start-4 font-semibold border-b border-row-separator">
							{NumberFormat(totalPercent, true)}%
						</div>
						<div class="border-b border-row-separator">Result</div>
						<div class="border-b border-row-separator">Hold</div>
						<div class="border-b border-row-separator">Result</div>
						<div class="border-b border-row-separator">Hold</div>
						<div class="border-b border-row-separator">Bets</div>
						<!-- MARKET -->
						{#each filteredSelections as selection (selection.id)}
							<div
								class="col-start-1 py-2 text-left border-b border-row-separator h-8"
								class:col-span-2={market.is_manual && !market.is_derived}
								on:mouseenter={() => showEditSelection(selection)}
								on:mouseleave={() => {
									displayEditSelection = null;
								}}
								role="button"
								tabindex="-1"
							>
								<div class="flex flex-row justify-between items-center h-full">
									<div
										class="flex flex-row flex-grow items-center h-full min-w-[374.34px]"
										class:opacity-75={!isMarketResulted &&
											(selectionIsResulted(selection) ||
												selectionIsSuspended(selection) ||
												selectionHasInvalidPrice(selection) ||
												selectionIsPending(selection))}
										class:col-span-2={market.is_manual && !market.rolling_type && !market.is_derived}
									>
										{#if editingMarketSelection == selection.id}
											<TextInput
												name="newSelectionName"
												type="text"
												className="w-full"
												inputClasses="p-0.5"
												bind:value={selection.name}
												required={true}
												placeholder="Enter selection name"
											/>
											<LoadingButton
												title="Save selection name"
												class="p-1 mx-2 text-white bg-success hover:bg-grey focus:outline-none flex items-center rounded"
												loading={updatingSelectionName}
												on:click={() => saveSelectionName(selection)}
											>
												<Icon name="tick" classes="block w-4 h-4 opacity-100 inline" />
											</LoadingButton>
											<button
												title="Cancel"
												class="p-1 text-white bg-danger hover:bg-grey focus:outline-none flex items-center rounded"
												on:click={() => {
													selection.name = currentSelectionName;
													currentSelectionName = null;
													editingMarketSelection = null;
												}}
											>
												<Icon name="cross" classes="block w-4 h-4 opacity-100 inline" />
											</button>
										{:else}
											{selection.name}
											{transformLine(selection, market)}
											{#if selection.status.id === SportSelectionStatus.Win}
												<div class="flex items-center">
													<div class="px-1 py-1 mx-1 ml-1 text-white bg-green-600 rounded text-tiny">Winner</div>
													{#if selection && selection.payout_percentage != null && !isNaN(selection.payout_percentage) && Number(selection.payout_percentage) !== 100}
														<span class="ml-1 text-green-600 text-tiny">
															({Number(selection.payout_percentage).toFixed(1)}%)
														</span>
													{/if}
												</div>
											{:else if selection.status.id === SportSelectionStatus.Lost}
												<div class="px-1 py-1 mx-1 ml-1 text-gray-100 bg-red-300 rounded text-tiny">Loss</div>
											{:else if selection.status.id === SportSelectionStatus.Void}
												<div class="px-1 py-1 mx-1 ml-1 text-gray-100 bg-orange-300 rounded text-tiny">Void</div>
											{:else if selection.status.id === SportSelectionStatus.Pending && !selectionIsSuspended(selection)}
												<div class="px-1 py-1 mx-1 ml-1 text-white bg-gray-500 rounded text-tiny">Pending</div>
											{:else if selectionIsSuspended(selection)}
												<div class="px-1 py-1 mx-1 ml-1 text-gray-100 bg-orange-500 rounded text-tiny">Suspended</div>
											{/if}
										{/if}
									</div>
									<div class="flex flex-row text-right">
										{#if displayEditSelection === selection.id && !selectionIsResulted(selection) && editingMarketSelection !== selection.id}
											<button
												title="Edit selection name"
												class="p-1 text-white bg-blue-500 hover:bg-grey focus:outline-none flex items-center rounded"
												on:click={() => {
													if (editingMarketSelection !== null) {
														for (let selectionList of filteredSelections) {
															if (selectionList.id == editingMarketSelection) {
																selectionList.name = currentSelectionName;
																break;
															}
														}
													}
													currentSelectionName = selection.name;
													editingMarketSelection = selection.id;
												}}
											>
												<Icon name="pencil" classes="block w-4 h-4 opacity-100 inline" />
											</button>
										{/if}
										{#if selectionIsResulted(selection)}
											{#if marketCanBeUnresulted && selection.rolling_type == null}
												<button
													on:click={() => {
														promptUnresultSelection = selection;
														showPromptUnresultSelection = true;
													}}
													title="Unresult selection"
													class="mx-1"
												>
													<Icon name="unfinalise" classes="block w-5 h-5 opacity-100 inline" />
												</button>
											{:else}
												<span title="Can not unresult selection" class="mx-1">
													<Icon name="unfinalised-locked" classes="block w-5 h-5 text-gray-500 inline" />
												</span>
											{/if}
										{/if}
									</div>
								</div>
							</div>
							{#if !market.is_manual || market.is_derived}
								<div class="py-2 border-b border-row-separator h-8">
									<div
										class="inline-flex items-center h-full"
										class:opacity-75={!isMarketResulted &&
											(selectionIsResulted(selection) ||
												selectionIsSuspended(selection) ||
												selectionHasInvalidPrice(selection) ||
												selectionIsPending(selection))}
									>
										{#if !market.is_derived && selectionIsSuspended(selection)}
											SUS
										{:else if !market.is_derived && selectionHasInvalidPrice(selection)}
											-
										{:else}
											{NumberFormat(selection.price, false, false)}
										{/if}
									</div>
								</div>
							{/if}
							<div class="py-2 border-b border-row-separator h-8">
								<div
									class="flex items-center justify-center w-full h-full"
									class:opacity-75={!isMarketResulted && selectionIsResulted(selection)}
								>
									{#if (selectionIsSuspended(selection) && !hasLockedPrices) || (selectionIsSuspended(selection) && market.is_derived && !hasLockedPrices)}
										<span class="opacity-75">
											SUS <span class="ml-1">[{overridePrices[selection.id]}]</span>
										</span>
									{:else if selectionHasInvalidPrice(selection)}
										<span class="opacity-75"> - </span>
									{:else if (!isMarketResulted && !selectionIsResulted(selection)) || (!isMarketResulted && !selectionIsResulted(selection) && market.is_derived && hasLockedPrices)}
										<OverridePrice
											bind:value={overridePrices[selection.id]}
											{selection}
											bind:isEditingPrices
											isManual={market.is_manual && !market.is_derived}
											onEnter={saveOverridePrices}
										/>
									{:else}
										{overridePrices[selection.id]}
									{/if}
								</div>
							</div>
							<div class="py-2 border-b border-row-separator h-8">
								<div
									class="inline-flex items-center h-full"
									class:opacity-75={!isMarketResulted &&
										(selectionIsResulted(selection) ||
											selectionIsSuspended(selection) ||
											selectionHasInvalidPrice(selection) ||
											selectionIsPending(selection))}
								>
									{#if !selectionHasInvalidPrice(selection)}
										{#key overridePrices[selection.id]}
											{calcSinglePercentage(selection)}%
										{/key}
									{:else}
										-
									{/if}
								</div>
							</div>
							<div class="py-2 border-b border-row-separator h-8">
								<div
									class="inline-flex items-center h-full"
									class:opacity-75={!isMarketResulted &&
										(selectionIsResulted(selection) ||
											selectionIsSuspended(selection) ||
											selectionHasInvalidPrice(selection) ||
											selectionIsPending(selection))}
									class:text-red-500={parseFloat(formatLiability(selection, 'result')) < 0}
								>
									{formatLiability(selection, 'result')}
								</div>
							</div>
							<div class="py-2 border-b border-row-separator h-8">
								<div
									class="inline-flex items-center h-full"
									class:opacity-75={!isMarketResulted &&
										(selectionIsResulted(selection) ||
											selectionIsSuspended(selection) ||
											selectionHasInvalidPrice(selection) ||
											selectionIsPending(selection))}
								>
									{formatLiability(selection, 'hold')}
								</div>
							</div>
							<div class="py-2 border-b border-row-separator h-8">
								<div
									class="inline-flex items-center h-full"
									class:opacity-75={!isMarketResulted &&
										(selectionIsResulted(selection) ||
											selectionIsSuspended(selection) ||
											selectionHasInvalidPrice(selection) ||
											selectionIsPending(selection))}
									class:text-red-500={parseFloat(formatLiability(selection, 'multi_result')) < 0}
								>
									{formatLiability(selection, 'multi_result')}
								</div>
							</div>
							<div class="py-2 border-b border-row-separator h-8">
								<div
									class="inline-flex items-center h-full"
									class:opacity-75={!isMarketResulted &&
										(selectionIsResulted(selection) ||
											selectionIsSuspended(selection) ||
											selectionHasInvalidPrice(selection) ||
											selectionIsPending(selection))}
								>
									{formatLiability(selection, 'multi_hold')}
								</div>
							</div>
							<div class="py-2 border-b border-r border-row-separator h-8">
								<div
									class="inline-flex items-center h-full"
									class:opacity-75={!isMarketResulted &&
										(selectionIsResulted(selection) ||
											selectionIsSuspended(selection) ||
											selectionHasInvalidPrice(selection) ||
											selectionIsPending(selection))}
								>
									{(selection.liabilities && selection.liabilities.bet_count) || '-'}
								</div>
							</div>
						{/each}
					</div>
					<div class="flex justify-between">
						<div class="flex flex-row justify-start items-center space-x-3">
							{#if classification || (market.liabilities && market.liabilities.settings && market.liabilities.settings.selection_limit)}
								<div
									class="flex flex-shrink hover:border-blue-700 border-white border-2 hover:bg-blue-300 cursor-pointer my-2"
									on:click={() => {
										showLiabilityAdjustment = true;
									}}
								>
									<span>Liability Limit: {currentLiabilityLimit}</span>
									<span class="pl-3">Multi Liability Limit: {currentMultiLiabilityLimit}</span>
								</div>
							{:else}
								<div />
							{/if}
							{#if market?.type?.selection_win_limit_factor}
								<div
									class="flex flex-shrink hover:border-orange-700 border-white border-2 hover:bg-orange-300 cursor-pointer my-2"
								>
									Market factor: {market.type.selection_win_limit_factor}
								</div>
							{:else}
								<div />
							{/if}
						</div>
						{#if market.is_manual && market.status.id !== Status.Resulted && !market.rolling_type}
							<div
								class="flex flex-shrink hover:border-orange-700 border-white border-2 hover:bg-orange-300 cursor-pointer my-2"
								title="Add new selection to market"
								on:click={() => {
									showCreateMarketSelections = true;
								}}
							>
								Add new selections
							</div>
						{/if}
					</div>
				</div>
				{#if showOverrides}
					<div class="flex flex-col justify-between h-full space-y-1 bg-white">
						<div class="flex flex-col justify-center h-full space-y-0.5 text-sm px-1">
							<div
								class="flex justify-center text-tiny"
								class:h-3={!market.is_manual}
								class:h-6={(market.manual_result && market.status.id !== Status.Resulted) || market.is_manual}
								class:my-1={market.is_manual}
							>
								{#if (market.manual_result && market.status.id !== Status.Resulted) || market.is_manual}
									<button
										on:click={onManualResultClick}
										class="flex items-center justify-center bg-danger btn btn-primary-action w-full"
										class:text-tiny={!market.is_manual}
									>
										{#if market.is_manual}
											Result
										{:else}
											Manual Result
										{/if}
									</button>
								{/if}
							</div>
							{#if !isMarketResulted}
								{#if !market.is_manual}
									<span class="mb-1 font-semibold text-center"> Override Status </span>
								{/if}
								<button
									on:click={() => overrideStatus(Status.Closed)}
									class="flex items-center justify-center py-1 btn btn-primary-action hover:text-light"
									class:bg-red-300={market.override_status && market.override_status.id === Status.Closed}
									class:text-red-800={market.override_status && market.override_status.id === Status.Closed}
									class:bg-red-500={!(market.override_status && market.override_status.id === Status.Closed)}
								>
									{#if !market.is_manual}
										{#if market.override_status && market.override_status.id === Status.Closed}
											<Icon name="lock" classes="w-4 h-4 mr-1 text-red-800" />
										{:else}
											<Icon name="unlock" classes="w-4 h-4 mr-1" />
										{/if}
									{/if}
									<span class="flex-1 pt-px">Close</span>
								</button>
								<button
									on:click={() => overrideStatus(Status.Open)}
									class="flex items-center justify-start py-1 btn btn-primary-action hover:text-light"
									class:bg-green-300={market.override_status && market.override_status.id === Status.Open}
									class:text-green-800={market.override_status && market.override_status.id === Status.Open}
									class:bg-green-500={!(market.override_status && market.override_status.id === Status.Open)}
								>
									{#if !market.is_manual}
										{#if market.override_status && market.override_status.id === Status.Open}
											<Icon name="lock" classes="w-4 h-4 mr-1 text-green-800" />
										{:else}
											<Icon name="unlock" classes="w-4 h-4 mr-1" />
										{/if}
									{/if}
									<span class="flex-1 pt-px">Open</span>
								</button>
								<button
									on:click={() => overrideStatus(Status.Suspended)}
									class="flex items-center justify-center py-1 btn btn-primary-action hover:text-light"
									class:bg-orange-300={market.override_status && market.override_status.id === Status.Suspended}
									class:text-orange-800={market.override_status && market.override_status.id === Status.Suspended}
									class:bg-orange-500={!(market.override_status && market.override_status.id === Status.Suspended)}
								>
									{#if !market.is_manual}
										{#if market.override_status && market.override_status.id === Status.Suspended}
											<Icon name="lock" classes="w-4 h-4 mr-1 text-orange-800" />
										{:else}
											<Icon name="unlock" classes="w-4 h-4 mr-1" />
										{/if}
									{/if}
									<span class="flex-1 pt-px">Suspend</span>
								</button>
								<div class="mt-2 border-b" />
							{:else}
								<div class="mt-6" />
							{/if}
							{#if !isMarketResulted}
								{#if !market.is_manual}
									<ManualResultToggle {market} />
								{/if}
								<Boost {market} />
								<Bonus {market} />
								<Multis {market} />
								<Cashout {market} />
								<!--								<PreMatch {market} />
								<InPlay {market} />-->
							{/if}
						</div>
						{#if market.is_manual}
							<div class="flex flex-row items-center pt-1.5 text-xs justify-center">
								<button
									class="btn border-2 border-danger text-danger p-1 hover:bg-red-300 flex flex-rom items-center justify-center"
									on:click={() => (showPromptDeleteMarket = true)}
								>
									<span class="mr-2 font-semibold">Delete</span>
									<span>
										<Icon name="bin" />
									</span>
								</button>
							</div>
						{/if}
					</div>
				{/if}
			</div>
			<div>
				<div on:click={() => (showBets = !showBets)} class="w-full py-2 mt-1 text-sm bg-gray-200 cursor-pointer">
					<FontIcon icon="uniE90F" className="pl-2" />
					Bet list
				</div>
				{#if showBets}
					<MarketBets {market} {event} />
				{/if}
			</div>
		</div>
	</div>
{/if}

<style>
	.market-grid {
		grid-template-columns: 3.5fr repeat(8, 1fr);
	}
</style>
